import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const EMAILJS_SERVICE = 'service_jjo53al';
const EMAILJS_PUBLIC_KEY = 'tW7or3X7KMwezAXgV';
const EMAILJS_TEMPLATE_USER = 'template_utilisateur';
const EMAILJS_TEMPLATE_ADVERT = 'template_annonceur';

const FadeUp = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut', delay }}>
    {children}
  </motion.div>
);

const ADVERTISER_TARGETS = [
  { emoji: '🎬', label: 'Agences de casting' },
  { emoji: '🏫', label: 'Écoles de mode / comédie' },
  { emoji: '📸', label: 'Photographes professionnels' },
  { emoji: '💄', label: 'Marques beauté & cosmétiques' },
  { emoji: '👗', label: 'Marques de mode & prêt-à-porter' },
  { emoji: '💪', label: 'Marques fitness & sport' },
  { emoji: '💅', label: 'Instituts esthétique & bien-être' },
  { emoji: '🏢', label: 'Agences de mannequins' },
  { emoji: '🎭', label: 'Compagnies théâtre & production' },
  { emoji: '🏆', label: 'Clubs & fédérations sportives' },
  { emoji: '🎪', label: 'Événementiel & shows' },
  { emoji: '✳️', label: 'Autre' },
];

export default function Contact() {
  const [siteLang, setSiteLang] = useState('FR');
  const [activeTab, setActiveTab] = useState('user');
  const [userForm, setUserForm] = useState({ nom: '', email: '', message: '' });
  const [advertForm, setAdvertForm] = useState({ societe: '', nom: '', email: '', telephone: '', secteur: '', message: '' });
  const [userSent, setUserSent] = useState(false);
  const [advertSent, setAdvertSent] = useState(false);
  const [userSending, setUserSending] = useState(false);
  const [userError, setUserError] = useState(false);
  const [advertSending, setAdvertSending] = useState(false);
  const [advertError, setAdvertError] = useState(false);

  const handleUserSubmit = (e) => {
    e.preventDefault();
    setUserSending(true);
    setUserError(false);
    emailjs.send(
      EMAILJS_SERVICE,
      EMAILJS_TEMPLATE_USER,
      { nom: userForm.nom, email: userForm.email, message: userForm.message },
      EMAILJS_PUBLIC_KEY
    ).then(() => {
      setUserSent(true);
      setUserSending(false);
    }).catch(() => {
      setUserError(true);
      setUserSending(false);
    });
  };

  const handleAdvertSubmit = (e) => {
    e.preventDefault();
    setAdvertSending(true);
    setAdvertError(false);
    emailjs.send(
      EMAILJS_SERVICE,
      EMAILJS_TEMPLATE_ADVERT,
      {
        societe: advertForm.societe,
        nom: advertForm.nom,
        email: advertForm.email,
        telephone: advertForm.telephone,
        secteur: advertForm.secteur,
        message: advertForm.message
      },
      EMAILJS_PUBLIC_KEY
    ).then(() => {
      setAdvertSent(true);
      setAdvertSending(false);
    }).catch(() => {
      setAdvertError(true);
      setAdvertSending(false);
    });
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', border: '1px solid #e0e0e0',
    fontSize: '13px', backgroundColor: '#fff', color: '#1A1A1A',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif'
  };

  const labelStyle = {
    fontSize: '10px', letterSpacing: '2px', color: '#888',
    display: 'block', marginBottom: '6px', fontWeight: '700'
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <Header siteLang={siteLang} setSiteLang={setSiteLang} />

      {/* HERO */}
      <section style={{ backgroundColor: '#0e0e0e', padding: '80px 24px', textAlign: 'center' }}>
        <FadeUp>
          <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700' }}>CONTACT</p>
          <h1 style={{ color: '#fff', fontSize: '38px', fontWeight: '900', letterSpacing: '2px', margin: '0', textTransform: 'uppercase', lineHeight: 1.1 }}>
            {siteLang === 'FR' ? 'ON EST LÀ POUR VOUS' : 'WE\'RE HERE FOR YOU'}
          </h1>
        </FadeUp>
      </section>

      {/* TABS */}
      <section style={{ backgroundColor: '#f7f7f7', padding: '60px 24px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>

          {/* Sélecteur */}
          <FadeUp>
            <div style={{ display: 'flex', marginBottom: '48px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
              {[
                { id: 'user', label: siteLang === 'FR' ? '👤 UTILISATEUR' : '👤 USER' },
                { id: 'advertiser', label: siteLang === 'FR' ? '📢 ANNONCEUR' : '📢 ADVERTISER' },
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                  flex: 1, padding: '16px', border: 'none', cursor: 'pointer',
                  backgroundColor: activeTab === tab.id ? '#FF0066' : '#fff',
                  color: activeTab === tab.id ? '#fff' : '#888',
                  fontSize: '11px', fontWeight: '700', letterSpacing: '2px',
                  transition: 'all 0.2s'
                }}>{tab.label}</button>
              ))}
            </div>
          </FadeUp>

          {/* Formulaire utilisateur */}
          {activeTab === 'user' && (
            <FadeUp>
              {userSent ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  style={{ textAlign: 'center', padding: '48px' }}>
                  <p style={{ fontSize: '40px', margin: '0 0 16px' }}>✓</p>
                  <p style={{ color: '#1A1A1A', fontSize: '18px', fontWeight: '700', margin: '0 0 8px' }}>
                    {siteLang === 'FR' ? 'Message envoyé !' : 'Message sent!'}
                  </p>
                  <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>
                    {siteLang === 'FR' ? 'Nous vous répondons sous 48h.' : 'We\'ll get back to you within 48h.'}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleUserSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>{siteLang === 'FR' ? 'NOM' : 'NAME'}</label>
                    <input style={inputStyle} value={userForm.nom} onChange={e => setUserForm(p => ({ ...p, nom: e.target.value }))} required />
                  </div>
                  <div>
                    <label style={labelStyle}>EMAIL</label>
                    <input type="email" style={inputStyle} value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} required />
                  </div>
                  <div>
                    <label style={labelStyle}>{siteLang === 'FR' ? 'MESSAGE' : 'MESSAGE'}</label>
                    <textarea style={{ ...inputStyle, height: '140px', resize: 'vertical' }} value={userForm.message} onChange={e => setUserForm(p => ({ ...p, message: e.target.value }))} required />
                  </div>
                  {userError && <p style={{ color: '#FF0066', fontSize: '12px', textAlign: 'center' }}>{siteLang === 'FR' ? 'Erreur lors de l\'envoi. Réessayez.' : 'Sending failed. Please try again.'}</p>}
                  <motion.button type="submit" disabled={userSending} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    style={{ backgroundColor: userSending ? '#ccc' : '#FF0066', color: '#fff', border: 'none', padding: '16px', fontSize: '11px', letterSpacing: '3px', fontWeight: '700', cursor: userSending ? 'not-allowed' : 'pointer' }}>
                    {userSending ? '...' : siteLang === 'FR' ? 'ENVOYER' : 'SEND'}
                  </motion.button>
                </form>
              )}
            </FadeUp>
          )}

          {/* Formulaire annonceur */}
          {activeTab === 'advertiser' && (
            <FadeUp>
              {/* Bloc audience */}
              <div style={{ backgroundColor: '#0e0e0e', padding: '32px', marginBottom: '40px' }}>
                <p style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '3px', fontWeight: '700', margin: '0 0 8px' }}>
                  {siteLang === 'FR' ? 'VOTRE AUDIENCE VOUS ATTEND' : 'YOUR AUDIENCE IS WAITING'}
                </p>
                <p style={{ color: '#fff', fontSize: '14px', margin: '0 0 24px', lineHeight: 1.6, opacity: 0.8 }}>
                  {siteLang === 'FR'
                    ? 'Chaque mois, des milliers de mannequins et aspirants créent leur composite sur Compify. Une audience ciblée, qualifiée, passionnée.'
                    : 'Every month, thousands of models and aspiring models create their composite on Compify. A targeted, qualified, passionate audience.'}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  {ADVERTISER_TARGETS.map((target, i) => (
                    <motion.div key={i} whileHover={{ scale: 1.05 }}
                      style={{ backgroundColor: '#1a1a1a', padding: '12px 8px', textAlign: 'center', border: '1px solid #2a2a2a' }}>
                      <p style={{ fontSize: '20px', margin: '0 0 6px' }}>{target.emoji}</p>
                      <p style={{ color: '#aaa', fontSize: '9px', letterSpacing: '1px', margin: 0, fontWeight: '700' }}>{target.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>

              {advertSent ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  style={{ textAlign: 'center', padding: '48px' }}>
                  <p style={{ fontSize: '40px', margin: '0 0 16px' }}>✓</p>
                  <p style={{ color: '#1A1A1A', fontSize: '18px', fontWeight: '700', margin: '0 0 8px' }}>
                    {siteLang === 'FR' ? 'Demande envoyée !' : 'Request sent!'}
                  </p>
                  <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>
                    {siteLang === 'FR' ? 'Notre équipe vous contacte sous 24h.' : 'Our team will contact you within 24h.'}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleAdvertSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>{siteLang === 'FR' ? 'SOCIÉTÉ' : 'COMPANY'}</label>
                      <input style={inputStyle} value={advertForm.societe} onChange={e => setAdvertForm(p => ({ ...p, societe: e.target.value }))} required />
                    </div>
                    <div>
                      <label style={labelStyle}>{siteLang === 'FR' ? 'NOM' : 'NAME'}</label>
                      <input style={inputStyle} value={advertForm.nom} onChange={e => setAdvertForm(p => ({ ...p, nom: e.target.value }))} required />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>EMAIL</label>
                      <input type="email" style={inputStyle} value={advertForm.email} onChange={e => setAdvertForm(p => ({ ...p, email: e.target.value }))} required />
                    </div>
                    <div>
                      <label style={labelStyle}>{siteLang === 'FR' ? 'TÉLÉPHONE' : 'PHONE'}</label>
                      <input style={inputStyle} value={advertForm.telephone} onChange={e => setAdvertForm(p => ({ ...p, telephone: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>{siteLang === 'FR' ? 'SECTEUR D\'ACTIVITÉ' : 'INDUSTRY'}</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={advertForm.secteur} onChange={e => setAdvertForm(p => ({ ...p, secteur: e.target.value }))} required>
                      <option value="">{siteLang === 'FR' ? 'Choisissez...' : 'Choose...'}</option>
                      {ADVERTISER_TARGETS.map((t, i) => <option key={i} value={t.label}>{t.label}</option>)}
                      <option value="autre">{siteLang === 'FR' ? 'Autre' : 'Other'}</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>{siteLang === 'FR' ? 'VOTRE MESSAGE' : 'YOUR MESSAGE'}</label>
                    <textarea style={{ ...inputStyle, height: '120px', resize: 'vertical' }}
                      placeholder={siteLang === 'FR' ? 'Décrivez votre projet publicitaire, budget, objectifs...' : 'Describe your advertising project, budget, goals...'}
                      value={advertForm.message} onChange={e => setAdvertForm(p => ({ ...p, message: e.target.value }))} required />
                  </div>
                  {advertError && <p style={{ color: '#FF0066', fontSize: '12px', textAlign: 'center' }}>{siteLang === 'FR' ? 'Erreur lors de l\'envoi. Réessayez.' : 'Sending failed. Please try again.'}</p>}
                  <motion.button type="submit" disabled={advertSending} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    style={{ backgroundColor: advertSending ? '#ccc' : '#FF0066', color: '#fff', border: 'none', padding: '16px', fontSize: '11px', letterSpacing: '3px', fontWeight: '700', cursor: advertSending ? 'not-allowed' : 'pointer' }}>
                    {advertSending ? '...' : siteLang === 'FR' ? 'ENVOYER MA DEMANDE' : 'SEND MY REQUEST'}
                  </motion.button>
                </form>
              )}
            </FadeUp>
          )}
        </div>
      </section>

      <Footer siteLang={siteLang} />
    </div>
  );
}